import { computed, inject, Injectable, signal } from '@angular/core';
import {
  IQBoostConversationStore,
  IQChat,
  IQChatFrom,
  LangGraphConfirmation,
  LangGraphRunOptions,
  LangGraphService,
  LangGraphStreamUpdate,
  LangGraphThreadScope,
  toChatHistory,
} from '@maximizer/ai/data-access';
import {
  catchError,
  concatWith,
  defer,
  EMPTY,
  finalize,
  forkJoin,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { readChatHistory } from './iqboost-chat-history';
import { IQBoostChatRun, IQBoostChatRunOwner } from './iqboost-chat-run';

export interface IQBoostChatPrompt {
  readonly content: string;
  readonly options: LangGraphRunOptions;
}

export interface IQBoostChatRequest {
  // Titles the thread when the question starts a new one.
  readonly title: string;
  readonly scope: LangGraphThreadScope;
  readonly prompt: Observable<IQBoostChatPrompt>;
}

// One for the application: a run goes on whatever the dialog shows, and once it closes.
@Injectable({ providedIn: 'root' })
export class IQBoostChatRunStore {
  private readonly langGraph = inject(LangGraphService);
  private readonly runs = signal<readonly IQBoostChatRun[]>([]);
  private readonly streams = new Map<IQBoostChatRun, Subscription>();
  private readonly finishedRuns = new Subject<IQBoostChatRun>();

  readonly finished = this.finishedRuns.asObservable();
  readonly busyThreadIds = computed<ReadonlySet<string>>(
    () =>
      new Set(
        this.runs().flatMap((run) => {
          const threadId = run.threadId();
          return threadId ? [threadId] : [];
        }),
      ),
  );

  of(conversation: IQBoostConversationStore, threadId: string | null): IQBoostChatRun | undefined {
    return this.runs().find(
      (run) =>
        run.owner.conversation === conversation &&
        run.threadId() === threadId &&
        (threadId !== null || isQuestionShown(run)),
    );
  }

  // The thread is created, the files read and the prompt looked up at the same time.
  ask(run: IQBoostChatRun, { title, scope, prompt }: IQBoostChatRequest): void {
    this.start(
      run,
      forkJoin([this.threadOf(run, title, scope), prompt]).pipe(
        switchMap(([threadId, { content, options }]) =>
          this.langGraph.runStream(content, threadId, options),
        ),
      ),
    );
  }

  start(run: IQBoostChatRun, updates: Observable<LangGraphStreamUpdate>): void {
    this.runs.update((runs) => [...runs, run]);
    const stream = updates
      .pipe(
        concatWith(defer(() => this.resume(run))),
        tap((update) => run.apply(update)),
        catchError(() => {
          run.fail();
          return EMPTY;
        }),
        finalize(() => this.finish(run)),
      )
      .subscribe();
    if (!stream.closed) {
      this.streams.set(run, stream);
    }
  }

  join(owner: IQBoostChatRunOwner, threadId: string): void {
    if (this.runs().some((run) => run.threadId() === threadId)) {
      return;
    }
    const run = new IQBoostChatRun(owner, { joined: true });
    run.threadId.set(threadId);
    this.start(
      run,
      this.langGraph
        .findActiveRun(threadId)
        .pipe(
          switchMap((runId) => (runId ? this.langGraph.joinRunStream(threadId, runId) : EMPTY)),
        ),
    );
  }

  // Only the stream stops here: deleting the thread stops the server's run.
  cancel(threadId: string): void {
    const run = this.runs().find((item) => item.threadId() === threadId);
    if (run) {
      run.cancelled = true;
      this.streams.get(run)?.unsubscribe();
    }
  }

  // Kept by the conversation only while it still shows the question: closed and opened on another
  // household meanwhile, the chat on screen is someone else's.
  private threadOf(
    run: IQBoostChatRun,
    title: string,
    scope: LangGraphThreadScope,
  ): Observable<string> {
    const existing = run.threadId();
    if (existing) {
      return of(existing);
    }
    return this.langGraph.createThread(title, scope).pipe(
      tap((threadId) => {
        const shown = isQuestionShown(run);
        run.threadId.set(threadId);
        if (shown) {
          run.owner.conversation.setThreadId(threadId);
          run.owner.conversation.addConversation({
            threadId,
            title,
            updatedAt: new Date().toISOString(),
          });
        }
      }),
    );
  }

  // A stream that closes before `end` was cut: a run still at work is joined again from its start.
  private resume(run: IQBoostChatRun): Observable<LangGraphStreamUpdate> {
    const threadId = run.threadId();
    if (run.ended || run.joined || !threadId) {
      return EMPTY;
    }
    return this.langGraph.findActiveRun(threadId).pipe(
      switchMap((runId) => {
        if (!runId) {
          return EMPTY;
        }
        run.restart();
        return this.langGraph.joinRunStream(threadId, runId);
      }),
      // The thread is read instead once the run is over.
      catchError(() => EMPTY),
    );
  }

  private finish(run: IQBoostChatRun): void {
    this.streams.delete(run);
    const threadId = run.threadId();
    // A run that asked or answered a confirmation shows as the thread saved it, as on a reopen.
    if (
      !run.cancelled &&
      threadId &&
      (run.joined || run.resumed || run.waiting() || (!run.failed && !run.ended))
    ) {
      this.settle(run, threadId);
      return;
    }
    this.release(run);
    if (run.cancelled) {
      this.finishedRuns.next(run);
      return;
    }
    run.claimed?.complete({ threadId, answer: run.answer(), ended: run.ended });
    if (threadId && run.owner.conversation.threadId() === threadId) {
      if (run.ended) {
        this.commit(run);
      } else if (run.failed) {
        this.keepHandoffs(run);
      }
    }
    this.finishedRuns.next(run);
  }

  private release(run: IQBoostChatRun): void {
    this.runs.update((runs) => runs.filter((item) => item !== run));
  }

  private commit({ owner, claimed, shownAnswer, steps, handoffs }: IQBoostChatRun): void {
    const answer = shownAnswer();
    const addon = claimed?.addon ?? null;
    if (!answer && !addon && handoffs().length === 0) {
      return;
    }
    owner.conversation.appendMessage({
      content: answer ?? '',
      from: IQChatFrom.Ai,
      ...(steps().length > 0 ? { steps: steps() } : {}),
      ...(addon ? { addon } : {}),
      ...(handoffs().length > 0 ? { handoffs: handoffs() } : {}),
    });
  }

  // The runs it handed off go on without it: they stay under an empty answer, as its reopened
  // thread shows them.
  private keepHandoffs({ owner, handoffs }: IQBoostChatRun): void {
    if (handoffs().length > 0) {
      owner.conversation.appendMessage({ content: '', from: IQChatFrom.Ai, handoffs: handoffs() });
    }
  }

  // The stream never said the run ended, so what the thread saved is the answer. The run stays busy
  // until it shows, so nothing can be asked on the thread, nor a confirmation answered twice.
  private settle(run: IQBoostChatRun, threadId: string): void {
    const { conversation, overrides } = run.owner;
    if (conversation.threadId() !== threadId && !run.claimed) {
      this.release(run);
      this.finishedRuns.next(run);
      return;
    }
    const shown = conversation.chatHistory();
    this.langGraph
      .getThreadConversation(threadId)
      .pipe(catchError(() => of(null)))
      .subscribe((thread) => {
        this.release(run);
        if (run.cancelled) {
          this.finishedRuns.next(run);
          return;
        }
        const [asked, last] = thread ? toChatHistory(thread.messages).slice(-2) : [];
        // Only an answer to this run's own question: the one before it belongs to another run.
        const answered =
          last?.from === IQChatFrom.Ai && (!run.question || asked?.content === run.question.content);
        const answer = answered ? last.content : '';
        if (answer) {
          run.ended = true;
          run.answer.set(answer);
        }
        // Unread, the thread waits on what the stream asked.
        const asking = (thread?.confirmations ?? run.confirmations()).find(
          ({ data }) => data.status === 'pending',
        );
        if (asking) {
          run.apply({ kind: 'confirmation', confirmation: asking });
          if (!run.failed) {
            run.ended = true;
          }
        }
        run.failed = !run.joined && !run.ended;
        run.claimed?.complete({ threadId, answer, ended: run.ended });
        // A question asked meanwhile stays: the thread has its answer the next time it is opened.
        if (conversation.threadId() === threadId && conversation.chatHistory() === shown) {
          if (thread) {
            conversation.openConversation(threadId, readChatHistory(thread, overrides));
          } else if (run.resumed || run.waiting()) {
            conversation.openConversation(threadId, streamedHistory(run, shown));
          }
        }
        this.finishedRuns.next(run);
      });
  }
}

// The thread could not be read, so it shows what the run streamed, saved as `commit` saves an
// answer: its decisions on the cards above, then what it answered, handed off or asks.
function streamedHistory(run: IQBoostChatRun, shown: IQChat[]): IQChat[] {
  const streamed = run.confirmations();
  const latest = new Map(streamed.map((confirmation) => [confirmation.id, confirmation]));
  const latestOf = (confirmation: LangGraphConfirmation) =>
    latest.get(confirmation.id) ?? confirmation;
  const history = shown.map((chat) =>
    chat.confirmations ? { ...chat, confirmations: chat.confirmations.map(latestOf) } : chat,
  );

  const listed = new Set(shown.flatMap((chat) => chat.confirmations ?? []).map(({ id }) => id));
  const confirmations = streamed.filter(({ id }) => !listed.has(id));
  const answer = run.failed ? null : run.shownAnswer();
  const steps = run.failed ? [] : run.steps();
  const handoffs = run.handoffs();
  if (!answer && handoffs.length === 0 && confirmations.length === 0) {
    return history;
  }
  return [
    ...history,
    {
      content: answer ?? '',
      from: IQChatFrom.Ai,
      ...(steps.length > 0 ? { steps } : {}),
      ...(handoffs.length > 0 ? { handoffs } : {}),
      ...(confirmations.length > 0 ? { confirmations } : {}),
    },
  ];
}

function isQuestionShown({ owner, question }: IQBoostChatRun): boolean {
  const { conversation } = owner;
  return conversation.threadId() === null && conversation.chatHistory().at(-1) === question;
}
